import { useEffect, useMemo, useRef, useState } from 'react';
import { Anchor, CSSProperties, Tooltip } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { useTheme } from '../theme/ThemeProvider';

interface ThemeToggleProps {
  showOnMobile: boolean
}

export function ThemeToggle({showOnMobile}: ThemeToggleProps) {
  const {computedColorScheme, toggleTheme} = useTheme();
  const reduceMotion = useReducedMotion();
  const isDark = computedColorScheme === 'dark';
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  const config = {width: 50, height: 25};

  // The component now defaults to the correct final position.
  const [position, setPosition] = useState(isDark ? 40 : 0);
  const animationRef = useRef(0);
  const targetPositionRef = useRef(isDark ? 40 : 0);
  const currentPositionRef = useRef(isDark ? 40 : 0);

  // Generate unique IDs for this instance to prevent SVG element conflicts
  const ids = useMemo(() => {
    const suffix = Math.random().toString(36).substring(7);
    return {
      backgroundGradient: `backgroundGradient-${suffix}`,
      dropShadow: `dropShadow-${suffix}`,
      mainClip: `mainClip-${suffix}`
    };
  }, []);

  // Animate position when isDark changes, respecting the user's motion preference
  useEffect(() => {
    targetPositionRef.current = isDark ? 40 : 0;

    // If the user prefers reduced motion, we skip the animation entirely.
    if (reduceMotion) {
      setPosition(targetPositionRef.current);
      currentPositionRef.current = targetPositionRef.current;
      return;
    }

    const animate = () => {
      const current = currentPositionRef.current;
      const target = targetPositionRef.current;
      const diff = target - current;

      // Stop animating when we're very close to the target
      if (Math.abs(diff) > 0.1) {
        // A gentle easing function for a smoother animation
        currentPositionRef.current += diff * 0.1;
        setPosition(currentPositionRef.current);
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Snap to the final position
        currentPositionRef.current = target;
        setPosition(target);
      }
    };

    // Cancel any previous animation frame before starting a new one
    cancelAnimationFrame(animationRef.current);
    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [isDark, reduceMotion]); // Re-run the effect if the theme or motion preference changes

  // Interpolated values based on the animated 'position' state
  const interpolate = (startValue: number, endValue: number) => {
    const t = position / 40; // t goes from 0 (light) to 1 (dark)
    return startValue + (endValue - startValue) * t;
  };

  // Opacities are calculated based on the animation's progress
  const sunOpacity = 1 - (position / 40);
  const moonOpacity = position / 40;
  const cloudsOpacity = 0.7 * (1 - position / 40);
  const starsOpacity = position / 40;
  const sunRayRotation = interpolate(0, -45);

  const handleToggle = () => {
    // Use the correct toggle implementation that respects computed color scheme
    toggleTheme();
  };

  // Styles to replace the removed Tailwind classes
  const toggleStyles: CSSProperties = {
    position: 'relative',
    display: 'block',
    width: config.width,
    height: config.height,
    minWidth: config.width,
    borderRadius: '9999px',
    overflow: 'hidden',
    cursor: 'pointer',
  };


  return (
    <Tooltip
      hiddenFrom={showOnMobile ? 'md' : undefined}
      visibleFrom={showOnMobile ? undefined : 'md'}
      label={label}
      position="bottom"
    >
      <Anchor
        onClick={handleToggle}
        style={toggleStyles}
        aria-label={label}
        hiddenFrom={showOnMobile ? 'md' : undefined}
        visibleFrom={showOnMobile ? undefined : 'md'}
      >
        <svg
          width={config.width}
          height={config.height}
          viewBox="0 0 80 40"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id={ids.backgroundGradient} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{stopColor: `rgb(${Math.round(interpolate(17, 37))}, ${Math.round(interpolate(122, 45))}, ${Math.round(interpolate(245, 55))})`}}/>
              <stop offset="100%" style={{stopColor: `rgb(${Math.round(interpolate(13, 26))}, ${Math.round(interpolate(92, 31))}, ${Math.round(interpolate(184, 40))})`}}/>
            </linearGradient>
            <filter id={ids.dropShadow} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000000" floodOpacity="0.2"/>
            </filter>
            <clipPath id={ids.mainClip}>
              <rect x="0" y="0" width="80" height="40" rx="20"/>
            </clipPath>
          </defs>
          <g clipPath={`url(#${ids.mainClip})`}>
            {/* Background */}
            <rect x="0" y="0" width="80" height="40" fill={`url(#${ids.backgroundGradient})`}/>

            {/* Day clouds */}
            <g style={{opacity: cloudsOpacity}} fill="#F3FDFF">
              <ellipse cx="65" cy="12" rx="5" ry="2.5" fillOpacity="0.8"/>
              <ellipse cx="68" cy="32" rx="4" ry="2" fillOpacity="0.5"/>
              <ellipse cx="58" cy="8" rx="4.5" ry="2" fillOpacity="0.6"/>
            </g>

            {/* Night stars */}
            <g style={{opacity: starsOpacity}} fill="white">
              <circle cx="12" cy="15" r="1.5"/>
              <circle cx="18" cy="8" r="1"/>
              <circle cx="25" cy="12" r="0.8"/>
              <circle cx="32" cy="28" r="1.2"/>
            </g>

            {/* Sun/Moon circle */}
            <g style={{transform: `translateX(${position}px)`}}>
              <circle cx="20" cy="20" r="14" fill={`rgb(${Math.round(interpolate(243, 201))}, ${Math.round(interpolate(185, 201))}, ${Math.round(interpolate(44, 201))})`} filter={`url(#${ids.dropShadow})`}/>

              {/* Sun rays - now with rotation! */}
              <g style={{opacity: sunOpacity}} fill="#F5E042" transform={`translate(20, 20) rotate(${sunRayRotation})`}>
                {[0, 45, 90, 135, 180, 225, 270, 315].map(rot => (
                  <rect key={rot} x="-0.5" y="-18" width="1" height="4" rx="0.5" transform={`rotate(${rot})`}/>
                ))}
              </g>

              {/* Moon craters */}
              <g style={{opacity: moonOpacity}} fill="#889399" fillOpacity="0.6">
                <circle cx="17" cy="13" r="2.5"/>
                <circle cx="15" cy="23" r="3.5"/>
                <circle cx="25" cy="22" r="2"/>
              </g>
            </g>
          </g>
        </svg>
      </Anchor>
    </Tooltip>
  );
}

